/**
 * Libraries
 */

import { createApp } from 'vue';
import { createHead } from '@unhead/vue';
import FloatingVue from 'floating-vue';

/**
 * I18n
 */

import i18n from '@shared/i18n';

/**
 * Configs
 */

import { setupSentry } from '@config/sentry';
import { isDev } from '@config/env';
import {
  setupPlausibleTracking,
  trackEvent,
  trackPageView,
} from '@config/plausible';

import pinia from '@config/pinia';

/**
 * Keycloak
 */

import { VueKeycloak } from '@core/keycloak/plugin';
import { keycloakInstance } from '@core/keycloak';

/**
 * Stores
 */

import { useKeycloakStore } from '@/stores/keycloak';

/**
 * Router
 */

import router from '@core/router';

/**
 * Assets
 */

import '@/assets/css/index.css';

/**
 * App
 */

import App from '@/App.vue';

const app = createApp(App)
  .use(pinia)
  .use(createHead())
  .use(VueKeycloak)
  .use(i18n)
  .use(FloatingVue, {
    themes: {
      'action-warning-icon': {
        $extend: 'tooltip',
        triggers: ['hover'],
      },
      'action-icon': {
        $extend: 'tooltip',
        triggers: ['hover'],
      },
      'base-dropdown': {
        $extend: 'dropdown',
      },
      'filter-dropdown': {
        $extend: 'dropdown',
      },
      'menu-open-sidebar': {
        $extend: 'dropdown',
      },
      mentionable: {
        $extend: 'dropdown',
      },
    },
  });

keycloakInstance.onAuthSuccess = () => {
  if (!isDev) setupSentry(app, router);

  const plausibleConfig = setupPlausibleTracking();
  app.use(router);
  app.mount('#app');

  const { getRoles, setKeycloakData } = useKeycloakStore();

  /**
   * We set the keycloak data here again because sometimes the data is not set correctly in the Keycloak plugin
   */
  if (keycloakInstance.tokenParsed) {
    setKeycloakData(keycloakInstance.tokenParsed);
  }

  if (plausibleConfig) {
    router.isReady().then(() => {
      const { path, query } = router.currentRoute.value;
      trackPageView(path, query);
      trackEvent('session_start', {
        screen: `${window.screen.width}x${window.screen.height}`,
        screenWidth: window.screen.width.toString(),
        screenHeight: window.screen.height.toString(),
        screenPixelDepth: window.screen.pixelDepth.toString(),
        screenOrientationType: window.screen.orientation.type,
      });
    });
  }

  /**
   * If the user has no roles, redirect to the unauthorized page
   */
  if (getRoles().length === 0) {
    router.push({ name: 'unauthorized' });
  }
};
